/**
 * ImmersiveFullscreen — tiered fullscreen for the LEGACY React player viewers
 * (video/embed $shareToken fallback, used only for not-yet-baked shares). Mirrors
 * the vplay engine's IvyFullscreen (gsap-framework/js/PlayerFullscreen.js), which
 * is the single source of truth for the baked CDN player that all shares use.
 * Kept as a separate small module because that engine is a window global set
 * inside the slide iframe, not importable in the parent React app.
 *
 * Tier: iOS (iPhone + iPad, every browser) ALWAYS uses the CSS overlay — never
 * native — so a drag can't trigger Safari's un-blockable swipe-to-exit. Desktop /
 * Android use native fullscreen. The visual overlay is React-driven off isActive();
 * this controller owns the tier decision, the immersive touch/scroll lock, the
 * embed handshake, and the post-transition audio resume.
 */

export interface ImmersiveFullscreenOpts {
  container: HTMLElement;
  isPlaying: () => boolean;
  resume: () => void;
  onChange?: (active: boolean) => void;
}

// webkit-prefixed fullscreen members aren't in the TS DOM lib (older Safari).
interface WebkitFullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
}
interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => void;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone|iPod|iPad/.test(ua)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
}

export class ImmersiveFullscreen {
  private container: HTMLElement;
  private isPlaying: () => boolean;
  private resume: () => void;
  private changeCb?: (active: boolean) => void;
  private cssActive = false;
  private locked = false;
  private saved: Record<string, string> | null = null;
  private blockScroll: ((e: Event) => void) | null = null;
  private wasPlayingHidden = false;
  private onFsChange = () => this.afterTransition();
  private onVis = () => this.visibility();

  constructor(opts: ImmersiveFullscreenOpts) {
    this.container = opts.container;
    this.isPlaying = opts.isPlaying;
    this.resume = opts.resume;
    this.changeCb = opts.onChange;
    document.addEventListener('fullscreenchange', this.onFsChange);
    document.addEventListener('webkitfullscreenchange', this.onFsChange as EventListener);
    document.addEventListener('visibilitychange', this.onVis);
  }

  isActive(): boolean {
    const d = document as WebkitFullscreenDocument;
    return !!(d.fullscreenElement || d.webkitFullscreenElement) || this.cssActive;
  }

  toggle(): void {
    const d = document as WebkitFullscreenDocument;
    if (d.fullscreenElement || d.webkitFullscreenElement) {
      (d.exitFullscreen || d.webkitExitFullscreen)?.call(document);
      return;
    }
    if (this.cssActive) { this.exitCss(); return; }
    if (isIOS()) { this.enterCss(); return; }
    const el = this.container as WebkitFullscreenElement;
    const req = el.requestFullscreen?.();
    if (req && typeof req.catch === 'function') req.catch(() => this.enterCss());
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else this.enterCss();
  }

  private enterCss(): void {
    this.cssActive = true;
    this.embedHandshake(true);
    this.afterTransition();
    try { requestAnimationFrame(() => { if (this.cssActive) window.scrollTo(0, 1); }); } catch { /* noop */ }
  }

  private exitCss(): void {
    this.cssActive = false;
    this.embedHandshake(false);
    this.afterTransition();
  }

  private embedHandshake(on: boolean): void {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ __ivy_player: true, type: 'immersive', on }, '*');
      }
    } catch { /* cross-origin parent — fine */ }
  }

  private immersiveLock(on: boolean): void {
    if (on === this.locked) return;
    this.locked = on;
    try {
      const c = this.container, de = document.documentElement, b = document.body;
      if (on) {
        this.saved = {
          cT: c.style.touchAction, cO: c.style.overscrollBehavior,
          bOv: b.style.overflow, bOs: b.style.overscrollBehavior, deOv: de.style.overflow,
        };
        c.style.touchAction = 'none';
        c.style.overscrollBehavior = 'none';
        b.style.overflow = 'hidden';
        b.style.overscrollBehavior = 'none';
        de.style.overflow = 'hidden';
        this.blockScroll = (e: Event) => { try { e.preventDefault(); } catch { /* noop */ } };
        document.addEventListener('touchmove', this.blockScroll, { passive: false });
      } else if (this.saved) {
        c.style.touchAction = this.saved.cT;
        c.style.overscrollBehavior = this.saved.cO;
        b.style.overflow = this.saved.bOv;
        b.style.overscrollBehavior = this.saved.bOs;
        de.style.overflow = this.saved.deOv;
        this.saved = null;
        if (this.blockScroll) { document.removeEventListener('touchmove', this.blockScroll); this.blockScroll = null; }
      }
    } catch { /* non-fatal */ }
  }

  private afterTransition(): void {
    const active = this.isActive();
    this.immersiveLock(active);
    try { this.changeCb?.(active); } catch { /* non-fatal */ }
    if (this.isPlaying()) setTimeout(() => this.resume(), 200);
  }

  private visibility(): void {
    if (document.visibilityState === 'hidden') this.wasPlayingHidden = this.isPlaying();
    else if (this.wasPlayingHidden) { this.wasPlayingHidden = false; setTimeout(() => this.resume(), 100); }
  }

  destroy(): void {
    document.removeEventListener('fullscreenchange', this.onFsChange);
    document.removeEventListener('webkitfullscreenchange', this.onFsChange as EventListener);
    document.removeEventListener('visibilitychange', this.onVis);
    this.immersiveLock(false);
  }
}
